import DATA from './toys.json';

export const TOY_KINDS = ['ballPlain', 'star', 'mitten', 'gingerbread', 'house', 'bell', 'stocking', 'snowman'] as const;
export type ToyKind = typeof TOY_KINDS[number];
export const TOY_LABELS: Record<ToyKind, string> = {
  ballPlain: 'Шар', star: 'Звезда', mitten: 'Варежка', gingerbread: 'Пряник',
  house: 'Домик', bell: 'Колокольчик', stocking: 'Носок', snowman: 'Снеговик',
};
/** Работа гостя: одна из восьми форм или «Своя игрушка», форму которой задаёт альфа картинки (контура в toys.json у неё нет). */
export const CUSTOM_KIND = 'custom' as const;
export type WorkKind = ToyKind | typeof CUSTOM_KIND;
export const CUSTOM_LABEL = 'Своя игрушка';
/** Формы только для 3D-ёлки (в 2D их нет: `TOY_KINDS` остаётся восемью). Контуры для них уже лежат в `toys.json`. */
export const TOY_KINDS_3D_ONLY = ['ball', 'ballFlake', 'giftBox', 'giftTall'] as const;
/** Все 12 форм 3D-ёлки: порядок первых восьми как в 2D (от него зависят коды маркеров), новые в конце. */
export const TREE3D_TOY_KINDS = [...TOY_KINDS, ...TOY_KINDS_3D_ONLY] as const;
export type Tree3dToyKind = typeof TREE3D_TOY_KINDS[number];
export type Tree3dWorkKind = Tree3dToyKind | typeof CUSTOM_KIND;
export const TOY_LABELS_3D_ONLY: Record<typeof TOY_KINDS_3D_ONLY[number], string> = {
  ball: 'Шар с узором', ballFlake: 'Шар со снежинкой', giftBox: 'Подарок', giftTall: 'Высокий подарок',
};
/** Виды работ продукта: формы и «Своя игрушка» последней. */
export const TREE2D_WORK_KINDS: readonly WorkKind[] = [...TOY_KINDS, CUSTOM_KIND];
export const TREE3D_WORK_KINDS: readonly Tree3dWorkKind[] = [...TREE3D_TOY_KINDS, CUSTOM_KIND];
/** Подписи всех 12 форм одним типизированным словарём. */
export const TOY_LABELS_3D: Record<Tree3dToyKind, string> = { ...TOY_LABELS, ...TOY_LABELS_3D_ONLY };
export const toyLabel = (kind: Tree3dWorkKind): string => (kind === CUSTOM_KIND ? CUSTOM_LABEL : TOY_LABELS_3D[kind]);
export const PALETTE = ['#ce4b48', '#e3a33f', '#e7ce59', '#43876b', '#5598bc', '#8d6ba8', '#e99aab', '#354a50', '#fffdf5'];
export const DRAW_SIZE = 640;

export function makeCanvas(size = DRAW_SIZE): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = size;
  return canvas;
}

export function context(canvas: HTMLCanvasElement): CanvasRenderingContext2D {
  const value = canvas.getContext('2d');
  if (!value) throw new Error('Этот браузер не поддерживает рисование на canvas.');
  return value;
}

/** Same silhouettes and orientation as the existing printed Xmas templates. */
export function toyPath(kind: Tree3dToyKind, size = DRAW_SIZE): Path2D {
  const path = new Path2D();
  DATA.toys[kind].outline.forEach(([x, y], index) => {
    const px = (x + .5) * size, py = (.5 - y) * size;
    if (index === 0) path.moveTo(px, py); else path.lineTo(px, py);
  });
  path.closePath();
  return path;
}

/** Контур игрушки как путь SVG в квадрате `size`: те же точки, что у `toyPath`, для силуэта поверх камеры. */
export function toyOutlineSvgPath(kind: Tree3dToyKind, size = DRAW_SIZE): string {
  const points = DATA.toys[kind].outline.map(([x, y], index) => `${index === 0 ? 'M' : 'L'}${((x + .5) * size).toFixed(1)} ${((.5 - y) * size).toFixed(1)}`);
  return `${points.join(' ')} Z`;
}

export function paintToy(g: CanvasRenderingContext2D, kind: Tree3dToyKind, drawing?: CanvasImageSource): void {
  const shape = toyPath(kind);
  g.clearRect(0, 0, DRAW_SIZE, DRAW_SIZE);
  g.save();
  g.clip(shape);
  g.fillStyle = '#fffdf5';
  g.fillRect(0, 0, DRAW_SIZE, DRAW_SIZE);
  if (drawing) g.drawImage(drawing, 0, 0, DRAW_SIZE, DRAW_SIZE);
  g.restore();
  g.strokeStyle = '#40544c'; g.lineWidth = 3.4; g.lineJoin = 'round';
  g.stroke(shape);
}

export function renderToy(kind: Tree3dToyKind, drawing?: CanvasImageSource): HTMLCanvasElement {
  const canvas = makeCanvas();
  paintToy(context(canvas), kind, drawing);
  return canvas;
}

function seededRandom(seed: number): () => number {
  let state = seed >>> 0;
  return () => { state = (Math.imul(state, 1664525) + 1013904223) >>> 0; return state / 4294967296; };
}

/** Intentionally uneven crayon marks, not replacements for actual guest artwork. */
export function sampleDrawing(kind: Tree3dToyKind, seed: number): HTMLCanvasElement {
  const canvas = makeCanvas(), g = context(canvas), random = seededRandom(seed + 31);
  const colors = [PALETTE[seed % 7], PALETTE[(seed + 3) % 7], PALETTE[(seed + 1) % 7]];
  g.save(); g.clip(toyPath(kind));
  const base = kind === 'gingerbread' ? '#ca915e' : kind === 'star' ? '#e8c541' : colors[0];
  g.strokeStyle = base; g.lineCap = 'round';
  // Separate translucent passes preserve white paper and visible pencil grain.
  for (let row = -180; row < 850; row += 10) {
    g.globalAlpha = .25 + random() * .25; g.lineWidth = 5 + random() * 7;
    g.beginPath(); g.moveTo(-50 + random() * 60, row);
    g.lineTo(650 - random() * 40, row + 200 + random() * 32); g.stroke();
  }
  g.globalAlpha = 1;
  if (kind === 'ballPlain') {
    g.save();
    const heart = new Path2D('M 320 425 C 265 370 160 305 177 238 C 194 171 275 171 320 233 C 365 171 446 171 463 238 C 480 305 375 370 320 425 Z');
    g.clip(heart); g.fillStyle = '#fffdf5'; g.fillRect(120, 130, 410, 340);
    g.strokeStyle = colors[1]; g.lineWidth = 7;
    for (let x = 140; x < 500; x += 9) {
      g.globalAlpha = .65 + random() * .3; g.beginPath();
      g.moveTo(x + random() * 8, 160); g.lineTo(x + 12 + random() * 10, 450); g.stroke();
    }
    g.restore();
    g.strokeStyle = colors[2]; g.lineWidth = 9; g.beginPath();
    for (let i = 0; i <= 8; i++) g.lineTo(125 + i * 48, 479 + (i % 2) * 35 + random() * 10);
    g.stroke();
  } else if (kind === 'gingerbread' || kind === 'snowman' || kind === 'star') {
    g.fillStyle = '#354a50';
    for (const x of [261, 368]) { g.beginPath(); g.ellipse(x, kind === 'star' ? 300 : 160, 11, 16, -.12, 0, Math.PI * 2); g.fill(); }
    g.strokeStyle = '#354a50'; g.lineWidth = 8; g.beginPath();
    g.arc(316, kind === 'star' ? 318 : 170, 53, .12, Math.PI - .12); g.stroke();
    if (kind !== 'star') for (let i = 0; i < 3; i++) {
      g.fillStyle = colors[1]; g.beginPath(); g.arc(316 + random() * 8, 290 + i * 74, 19, 0, Math.PI * 2); g.fill();
    }
  } else if (kind === 'house') {
    g.fillStyle = colors[1]; g.beginPath(); g.moveTo(90, 300); g.lineTo(320, 75); g.lineTo(550, 300); g.closePath(); g.fill();
    g.fillStyle = '#f9dc77'; g.fillRect(212, 330, 80, 94); g.fillRect(347, 330, 80, 94);
    g.strokeStyle = '#fffdf5'; g.lineWidth = 9;
    for (const x of [252, 387]) { g.beginPath(); g.moveTo(x, 325); g.lineTo(x, 430); g.stroke(); }
    g.fillStyle = colors[2]; g.fillRect(283, 464, 84, 160);
  } else {
    for (let i = 0; i < 14; i++) {
      g.fillStyle = i % 2 ? colors[1] : colors[2]; g.globalAlpha = .8;
      g.beginPath(); g.ellipse(90 + random() * 460, 90 + random() * 460, 14 + random() * 16, 14 + random() * 18, random(), 0, Math.PI * 2); g.fill();
    }
    g.globalAlpha = 1; g.strokeStyle = '#fffdf5'; g.lineWidth = 16;
    g.beginPath(); g.moveTo(20, 510); g.lineTo(620, 510); g.stroke();
  }
  g.restore();
  return canvas;
}

export function shapeThumbnail(kind: Tree3dToyKind): string {
  return renderToy(kind).toDataURL('image/png');
}
